// Meeting log — the changelog's primary source.
//
// Each `Meeting` is one synchronous discussion. Each `MeetingEntry` is one
// salient discussion point or decision from that meeting, citing who said it
// (verbatim) and linking to the resulting action(s). The changelog scene
// renders this as a meeting-first timeline with citation chips that open a
// drawer with the verbatim quote + action detail + shipped state.
//
// Single source of truth — the /changelog skill appends here when a meeting's
// outputs land (via /meeting), and flips `state: 'shipped'` with the commit
// ref when a delivery commit references the action ID.
//
// Curated. Not every action is logged here — only the discussion points worth
// surfacing on the meeting timeline. The full action register lives in
// docs/meetings/<date>-<slug>-actions.md.
//
// The seed documents the foundation v1 arc (salvage → parity close-out).
// Replace or extend when a fork's first real client meeting lands via /meeting.

export type ActionState =
  | 'shipped' // delivered in a version cut
  | 'in-flight' // spec scaffolded or implementation started; not shipped
  | 'decided' // QnA resolved but UI not yet built
  | 'planned' // routed in roadmap, not started
  | 'blocked'; // awaiting decision (qna pending)

export type ActionRef = {
  /** Full action id, e.g. 'kickoff.A1' — globally unique. */
  id: string;
  /** Short id, e.g. 'A1' — used on chips. */
  shortId: string;
  /** Action title from the actions file. */
  title: string;
  state: ActionState;
  /** Version where this landed, when shipped (e.g. 'v1.1.0'). */
  shippedIn?: string;
  /** Short SHA of the delivery commit. */
  shippedCommit?: string;
};

export type MeetingEntry = {
  /** Stable id within the meeting — usually mirrors the primary action id. */
  id: string;
  /** Short summary of the discussion point — 4-12 words. */
  text: string;
  /** Who said the load-bearing line. Order matters: primary actor first. */
  actors: string[];
  /** Verbatim quote from the transcript / cleaned input. */
  quote: string;
  /** One or more action refs that fell out of this discussion point. */
  actions: ActionRef[];
};

export type Meeting = {
  /** Slug-ish id used in URLs and cross-references. */
  id: string;
  /** ISO date the meeting happened. */
  date: string;
  /** Display title. */
  title: string;
  /** Attendees as first-name strings (or 'External' tags). */
  attendees: string[];
  /** Path to the cleaned input doc. */
  inputPath: string;
  /** Path to the structured actions file. */
  actionsPath: string;
  /** Entries shown on the timeline. Newest meaningful items first. */
  entries: MeetingEntry[];
};

// Newest meetings first — the timeline reads top-down.
export const MEETINGS: Meeting[] = [
  {
    // Written reviewer feedback (email), not a synchronous meeting — logged
    // here because it drove the v1.8.0 release. Tim decided every item on
    // 25 Sep; A9 (outreach contacts) is not build work and is not logged.
    id: 'feedback',
    date: '2026-09-25',
    title: 'OEM+ reviewer feedback — Jay & Paul',
    attendees: ['Jay', 'Paul', 'Tim'],
    inputPath: 'docs/meetings/inputs/2026-09-25-jay-paul-feedback.md',
    actionsPath: 'docs/meetings/2026-09-25-jay-paul-feedback-actions.md',
    entries: [
      {
        id: 'feedback.A1',
        text: 'Fictional OEM by default, not "for Komatsu"',
        actors: ['Jay', 'Tim'],
        quote:
          "Don't think we should label it as 'for Komatsu'. It is a nice gesture and shows we're really thinking of them, but also shows this as a mock rather than a demo. … Suggest we move back to a non-existent OEM branding and make the machines pink.",
        actions: [
          {
            id: 'feedback.A1',
            shortId: 'A1',
            title:
              'Fictional OEM (Meridian) by default; Komatsu as an opt-in "OEM identity" switch',
            state: 'shipped',
            shippedIn: 'v1.8.0',
            shippedCommit: '08e44d4',
          },
        ],
      },
      {
        id: 'feedback.A2',
        text: 'Assistant gave wrong, pre-canned answers',
        actors: ['Jay'],
        quote:
          'Tried to ask the AI assistant a few questions, but seemed to have an incorrect set of answers. Is this working on the ProjeX platform, or are all the answers pre-built in and generic',
        actions: [
          {
            id: 'feedback.A2',
            shortId: 'A2',
            title:
              'Assistant answers wrong / pre-canned: harden the deterministic engine',
            state: 'shipped',
            shippedIn: 'v1.8.0',
            shippedCommit: '17a018d',
          },
        ],
      },
      {
        id: 'feedback.A3',
        text: 'A mind-map of every connected section',
        actors: ['Jay'],
        quote:
          "just curious to see if there's an overarching 'mind-map' or WBS architecture where we can see all these connected sections / sub-sections?",
        actions: [
          {
            id: 'feedback.A3',
            shortId: 'A3',
            title: 'Mind-map / WBS of every section and sub-section',
            state: 'shipped',
            shippedIn: 'v1.8.0',
            shippedCommit: '83584c6',
          },
        ],
      },
      {
        id: 'feedback.A4',
        text: 'Route root causes to operations or maintenance',
        actors: ['Paul'],
        quote:
          'The other thing I would suggest is some of the observation might be operator issues and should go to the Operating Supervisor / Superintendent and Mine control team. … Whereas other items should be going to the reliability engineer in the Maintenance team or maintenance planner to schedule parts (ie brake pad replacement), so an ability to identify the root causes and push to the appropriate person would be ideal.',
        actions: [
          {
            id: 'feedback.A4',
            shortId: 'A4',
            title:
              'Root-cause routing: operator issues to Operations, equipment issues to Maintenance',
            state: 'shipped',
            shippedIn: 'v1.8.0',
            shippedCommit: '13c872e',
          },
        ],
      },
      {
        id: 'feedback.A5',
        text: 'Track shifts and drivers to target training',
        actors: ['Paul'],
        quote:
          'Ie rear brake temperature exceedance could be a particular driver and how he operates the machine, (although condition monitoring is indicating it could be worn brakes) so tracking shifts and who is driving could also be helpful to identify who needs additional training etc.',
        actions: [
          {
            id: 'feedback.A5',
            shortId: 'A5',
            title: 'Track shifts and operators; flag who needs training',
            state: 'shipped',
            shippedIn: 'v1.8.0',
            shippedCommit: '2c31a00',
          },
        ],
      },
      {
        id: 'feedback.A6',
        text: 'A driver tree to pick the first machine',
        actors: ['Paul'],
        quote: 'Along with the Driver tree to priorities which machine to targeted first.',
        actions: [
          {
            id: 'feedback.A6',
            shortId: 'A6',
            title: 'Driver tree to decide which machine to target first',
            state: 'shipped',
            shippedIn: 'v1.8.0',
            shippedCommit: 'e4a6bf7',
          },
        ],
      },
      {
        id: 'feedback.A7',
        text: 'SPC, FMEA, control plans and TARPs on machine data',
        actors: ['Paul'],
        quote:
          "The SPC module that Miles is building for OptiFlow would work with the data being collected on these machines, we may also be able to use the FMEA, Control Plans & TARP's for this as well.",
        actions: [
          {
            id: 'feedback.A7',
            shortId: 'A7',
            title: 'SPC on machine data (OptiFlow SPC module, Miles)',
            state: 'shipped',
            shippedIn: 'v1.8.0',
            shippedCommit: '2c31a00',
          },
          {
            id: 'feedback.A8',
            shortId: 'A8',
            title: 'FMEA, control plans and TARPs',
            state: 'shipped',
            shippedIn: 'v1.8.0',
            shippedCommit: '2c31a00',
          },
        ],
      },
    ],
  },
  {
    id: 'oem-plus',
    date: '2026-09-22',
    title: 'OEM+ proof of concept — Jay/Tim catch-up',
    attendees: ['Jay', 'Tim'],
    inputPath: 'docs/meetings/inputs/2026-09-22-jay-tim-oem-plus.md',
    actionsPath: 'docs/meetings/2026-09-22-jay-tim-oem-plus-actions.md',
    entries: [
      {
        id: 'oem-plus.A2',
        text: 'Lead with cost visibility, then predictive maintenance',
        actors: ['Jay', 'Tim'],
        quote:
          'Lead with two outcomes: full cost visibility across disparate systems, then predictive maintenance.',
        actions: [
          {
            id: 'oem-plus.A2',
            shortId: 'A2',
            title:
              'Executive-first landing: cost visibility then predictive maintenance',
            state: 'in-flight',
          },
        ],
      },
      {
        id: 'oem-plus.A3',
        text: 'Operational detail stays behind drill-downs',
        actors: ['Jay'],
        quote:
          'Keep detail (e.g. trucking) behind drill-downs, only opening them in response to a question.',
        actions: [
          {
            id: 'oem-plus.A3',
            shortId: 'A3',
            title: 'Detail behind drill-downs — tiered nav with TIER_MAX',
            state: 'in-flight',
          },
        ],
      },
      {
        id: 'oem-plus.A7',
        text: 'Intelligence layer answers "why not Power BI?"',
        actors: ['Jay'],
        quote:
          'Answer "why not just Power BI on steroids?" by showing an intelligence layer that brings siloed equipment and cost data together, gives a coherent narrative, and helps retain institutional knowledge as senior expertise exits.',
        actions: [
          {
            id: 'oem-plus.A7',
            shortId: 'A7',
            title: 'Fold the intelligence-layer value story into the journey',
            state: 'planned',
          },
        ],
      },
      {
        id: 'oem-plus.A1',
        text: 'Translate the HTML demo onto the mock foundation',
        actors: ['Jay', 'Tim'],
        quote:
          'Translate the HTML into the standard Airis mock foundation (consistent with the Projects mock) so it is consistent with the production data model and avoids a vaporware feel.',
        actions: [
          {
            id: 'oem-plus.A1',
            shortId: 'A1',
            title: 'Translate the OEM+ HTML demo onto the mock foundation',
            state: 'in-flight',
          },
        ],
      },
      {
        id: 'oem-plus.A4',
        text: 'Komatsu is a skin, not a second product',
        actors: ['Jay'],
        quote:
          'Komatsu: use the same OEM+ story as the immediate demo vehicle — no different from Sandvik. No distinct persona, screen set or UX variation.',
        actions: [
          {
            id: 'oem-plus.A4',
            shortId: 'A4',
            title: 'Client brand selector — Komatsu default, Meridian fallback',
            // Superseded by feedback.A1 (25 Sep): Meridian default, Komatsu
            // opt-in. The selector shipped in that final form.
            state: 'shipped',
            shippedIn: 'v1.8.0',
            shippedCommit: '08e44d4',
          },
          {
            id: 'oem-plus.A10',
            shortId: 'A10',
            title: '"for Komatsu" lockup wording and trademark use',
            // Resolved by feedback.A1: Meridian shows no "for" line; "for
            // Komatsu" appears only when Komatsu is deliberately selected.
            state: 'shipped',
            shippedIn: 'v1.8.0',
            shippedCommit: '08e44d4',
          },
        ],
      },
      {
        id: 'oem-plus.A6',
        text: 'Call it a proof of concept, never a mock',
        actors: ['Jay'],
        quote:
          "Externally call it a 'proof of concept' or 'early-stage demo' (or 'case study'), not a 'mock'. 'Mock' reads as still-in-development to executives.",
        actions: [
          {
            id: 'oem-plus.A6',
            shortId: 'A6',
            title: 'External terminology — "proof of concept", never "mock"',
            state: 'planned',
          },
        ],
      },
      {
        id: 'oem-plus.A5',
        text: 'Ship it as a shareable Cloudflare link',
        actors: ['Tim', 'Jay'],
        quote:
          "Set up a new GitHub repo; deploy on Cloudflare so it's a shareable live link.",
        actions: [
          {
            id: 'oem-plus.A5',
            shortId: 'A5',
            title: 'Deploy on Cloudflare as a shareable link behind Access',
            state: 'planned',
          },
        ],
      },
    ],
  },
  {
    id: 'changelog-hardening',
    date: '2026-08-12',
    title: 'Changelog pipeline hardening',
    attendees: ['Miles'],
    inputPath: 'docs/meetings/inputs/2026-08-12-changelog-hardening.md',
    actionsPath: 'docs/meetings/2026-08-12-changelog-hardening-actions.md',
    entries: [
      {
        id: 'changelog-hardening.A1',
        text: 'Registry-driven release notes in ChangelogScene',
        actors: ['Miles'],
        quote:
          'The changelog was hardcoded for v2 — forks with only v1 saw an empty release tab. Read every VERSION entry from the registry instead.',
        actions: [
          {
            id: 'changelog-hardening.A1',
            shortId: 'A1',
            title: 'Generic release notes from registry.ts',
            state: 'in-flight',
          },
        ],
      },
      {
        id: 'changelog-hardening.A2',
        text: 'Build gate for label ↔ changelog drift',
        actors: ['Miles'],
        quote:
          'Nothing in npm run build caught a registry label bump without a matching changelog heading — add check:changelog.',
        actions: [
          {
            id: 'changelog-hardening.A2',
            shortId: 'A2',
            title: 'check:changelog script wired into build',
            state: 'in-flight',
          },
        ],
      },
    ],
  },
  {
    id: 'parity-closeout',
    date: '2026-08-05',
    title: 'Parity close-out — projects model + module surfaces',
    attendees: ['Miles', 'Nirman'],
    inputPath: 'docs/meetings/inputs/2026-08-05-parity-closeout.md',
    actionsPath: 'docs/meetings/2026-08-05-parity-closeout-actions.md',
    entries: [
      {
        id: 'parity-closeout.A1',
        text: 'Projects portfolio + sidebar recomposition',
        actors: ['Miles'],
        quote:
          'Ship a real `/projects` index — metric tiles, search/sort, populated table — and recompose the sidebar per route (portfolio header, Modules nav inside a project, footer back-row).',
        actions: [
          {
            id: 'parity-closeout.A1',
            shortId: 'A1',
            title: 'Projects portfolio + sidebar recomposition',
            state: 'shipped',
            shippedIn: 'v1.2',
            shippedCommit: 'b388f24',
          },
        ],
      },
      {
        id: 'parity-closeout.A2',
        text: 'Project assistant as a project module',
        actors: ['Miles'],
        quote:
          "`/projects/<slug>/chat` renders the assistant as a project module, opening on the project's latest seeded thread; sidebar filters Pinned/Recents to the project.",
        actions: [
          {
            id: 'parity-closeout.A2',
            shortId: 'A2',
            title: 'Project AI assistant as a project module',
            state: 'shipped',
            shippedIn: 'v1.2',
            shippedCommit: 'b388f24',
          },
        ],
      },
      {
        id: 'parity-closeout.A3',
        text: 'Seven project module surfaces',
        actors: ['Nirman'],
        quote:
          'Mirror all seven ai-foundation project modules (workflows, editor, dashboards, tasks, etc.) as seeded surfaces — alpha richest, others stubbed but navigable.',
        actions: [
          {
            id: 'parity-closeout.A3',
            shortId: 'A3',
            title: 'Seven project module surfaces (P6d close-out)',
            state: 'shipped',
            shippedIn: 'v1.3',
            shippedCommit: '4977cd3',
          },
        ],
      },
      {
        id: 'parity-closeout.A4',
        text: 'Chat, settings, and admin depth pass',
        actors: ['Miles'],
        quote:
          'Close-out remainders from the P6 verification harness: composer collapse, ThreadNoticeRail, artifact viewer, SharePoint browse shell, system-prompt lifecycle, lobby poller, usage period selector.',
        actions: [
          {
            id: 'parity-closeout.A4',
            shortId: 'A4',
            title: 'Chat/settings/admin depth pass (P6d remainders)',
            state: 'shipped',
            shippedIn: 'v1.3',
            shippedCommit: '4977cd3',
          },
        ],
      },
    ],
  },
  {
    id: 'foundation-salvage',
    date: '2026-07-31',
    title: 'Foundation salvage — strip ProjeX to generic scaffold',
    attendees: ['Miles'],
    inputPath: 'docs/meetings/inputs/2026-07-31-foundation-salvage.md',
    actionsPath: 'docs/meetings/2026-07-31-foundation-salvage-actions.md',
    entries: [
      {
        id: 'foundation-salvage.A1',
        text: 'Cut v1 generic foundation from ProjeX',
        actors: ['Miles'],
        quote:
          'understand the full meeting → actions → roadmap → build doc → UI chain before stripping this repo into `Airis-Analytica/mock-ui-foundation`.',
        actions: [
          {
            id: 'foundation-salvage.A1',
            shortId: 'A1',
            title: 'Cut v1 generic foundation from ProjeX salvage',
            state: 'shipped',
            shippedIn: 'v1.0',
            shippedCommit: '00cddc4',
          },
        ],
      },
      {
        id: 'foundation-salvage.A2',
        text: 'Meeting-driven changelog stays',
        actors: ['Miles'],
        quote:
          'Keep the capture pipeline (`/meeting` → `/roadmap`), logic-doc drawer, meeting-driven changelog, version registry, and three-panel chrome.',
        actions: [
          {
            id: 'foundation-salvage.A2',
            shortId: 'A2',
            title: 'Keep meeting-driven changelog + version registry',
            state: 'shipped',
            shippedIn: 'v1.0',
            shippedCommit: '00cddc4',
          },
        ],
      },
      {
        id: 'foundation-salvage.A3',
        text: 'ai-foundation parity directive',
        actors: ['Nirman'],
        quote:
          'bring in and align with the ai-foundation … chat panel (surface only), left panel, admin pages, user settings. This allows us to keep it very close to show users what we shall be building and how the final product shall look.',
        actions: [
          {
            id: 'foundation-salvage.A3',
            shortId: 'A3',
            title: 'ai-foundation parity — tokens + sidebar + chat spine',
            state: 'shipped',
            shippedIn: 'v1.1',
            shippedCommit: '52168a8',
          },
        ],
      },
      {
        id: 'foundation-salvage.A4',
        text: 'OKF wiki port from ai-foundation',
        actors: ['Miles'],
        quote:
          'Port harness commands and the OKF wiki tooling from ai-foundation so downstream mocks inherit the same doc ladder and agent skills.',
        actions: [
          {
            id: 'foundation-salvage.A4',
            shortId: 'A4',
            title: 'OKF wiki port (scripts/okf + /wiki-* skills)',
            state: 'shipped',
            shippedIn: 'v1.1',
            shippedCommit: '8badb86',
          },
        ],
      },
      {
        id: 'foundation-salvage.A5',
        text: 'Adapt agent skills for foundation',
        actors: ['Miles'],
        quote:
          'The generic scaffold should reset those to placeholder module names while keeping every workflow mechanism intact.',
        actions: [
          {
            id: 'foundation-salvage.A5',
            shortId: 'A5',
            title: 'Adapt ProjeX-era agent skills for foundation',
            state: 'shipped',
            shippedIn: 'v1.1',
            shippedCommit: '8badb86',
          },
        ],
      },
    ],
  },
];

export function findEntry(
  meetingId: string,
  entryId: string,
): { meeting: Meeting; entry: MeetingEntry } | null {
  const meeting = MEETINGS.find((m) => m.id === meetingId);
  if (!meeting) return null;
  const entry = meeting.entries.find((e) => e.id === entryId);
  if (!entry) return null;
  return { meeting, entry };
}

const STATE_LABEL: Record<ActionState, string> = {
  shipped: 'shipped',
  'in-flight': 'in-flight',
  decided: 'decided',
  planned: 'planned',
  blocked: 'blocked',
};

export function actionStateLabel(state: ActionState): string {
  return STATE_LABEL[state];
}
